import React, { useState, useEffect, useCallback } from 'react';
import { getPushSubscriptores, togglePushUsuario, togglePushRol, getComunidades } from '../services/api';
import { useToast } from '../components/Toast';
import './NotificacionesPush.css';

const ROLES = [
  { value: '', label: 'Todos' },
  { value: 'administrador', label: 'Admin', color: '#92400e', bg: '#fef3c7' },
  { value: 'presidente',   label: 'Presidente', color: '#5b21b6', bg: '#ede9fe' },
  { value: 'vecino',       label: 'Vecino',    color: '#065f46', bg: '#d1fae5' },
  { value: 'conserje',     label: 'Conserje',  color: '#1e40af', bg: '#dbeafe' },
  { value: 'proveedor',    label: 'Proveedor', color: '#9d174d', bg: '#fce7f3' },
];

const ROL_LABELS = {
  administrador: { label: 'Administradores', color: '#92400e', bg: '#fef3c7', border: '#fcd34d' },
  presidente:    { label: 'Presidentes',     color: '#5b21b6', bg: '#ede9fe', border: '#c4b5fd' },
  vecino:        { label: 'Vecinos',         color: '#065f46', bg: '#d1fae5', border: '#6ee7b7' },
  conserje:      { label: 'Conserjes',       color: '#1e40af', bg: '#dbeafe', border: '#93c5fd' },
  proveedor:     { label: 'Proveedores',     color: '#9d174d', bg: '#fce7f3', border: '#f9a8d4' },
};

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      className={`push-toggle ${checked ? 'push-toggle--on' : 'push-toggle--off'}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-checked={checked}
      role="switch"
    >
      <span className="push-toggle-thumb" />
    </button>
  );
}

function Avatar({ nombre, apellidos }) {
  const initials = `${(nombre || ' ')[0]}${(apellidos || ' ')[0]}`.toUpperCase().trim();
  return <div className="push-avatar">{initials}</div>;
}

export default function NotificacionesPush({ user }) {
  const { showToast } = useToast();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [rolFiltro, setRolFiltro] = useState('');
  const [comunidades, setComunidades] = useState([]);
  const [comunidadFiltro, setComunidadFiltro] = useState('');
  const [toggling, setToggling] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState('');

  const isAdmin = user?.rol === 'administrador' || user?.rol === 'empleado';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (rolFiltro) filters.rol = rolFiltro;
      if (comunidadFiltro) filters.comunidad_id = comunidadFiltro;
      if (busqueda) filters.q = busqueda;
      const res = await getPushSubscriptores(filters);
      setUsuarios(res.data);
    } catch {
      showToast('Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [rolFiltro, comunidadFiltro, busqueda, showToast]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (isAdmin) {
      getComunidades().then(r => setComunidades(r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const handleToggle = async (userId, nuevoValor) => {
    setToggling(prev => new Set(prev).add(userId));
    try {
      await togglePushUsuario(userId, nuevoValor);
      setUsuarios(prev =>
        prev.map(u => u.id === userId ? { ...u, push_habilitado: nuevoValor } : u)
      );
    } catch {
      showToast('Error al actualizar', 'error');
    } finally {
      setToggling(prev => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const handleBulkRol = async (rol, activar) => {
    setBulkLoading(`${rol}-${activar}`);
    try {
      const res = await togglePushRol(rol, activar, comunidadFiltro || undefined);
      showToast(res.data.message, 'success');
      setUsuarios(prev =>
        prev.map(u => u.rol === rol ? { ...u, push_habilitado: activar } : u)
      );
    } catch {
      showToast('Error al actualizar el rol', 'error');
    } finally {
      setBulkLoading('');
    }
  };

  // Agrupar por rol
  const porRol = ROLES.filter(r => r.value).reduce((acc, r) => {
    const lista = usuarios.filter(u => u.rol === r.value);
    if (lista.length) acc[r.value] = lista;
    return acc;
  }, {});

  const rolesConUsuarios = Object.keys(porRol);
  const totalActivos = usuarios.filter(u => u.push_habilitado).length;
  const totalConDispositivo = usuarios.filter(u => u.tiene_token).length;

  return (
    <div className="push-page">
      {/* Header */}
      <div className="push-header">
        <div className="push-header-left">
          <div className="push-header-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              <line x1="12" y1="2" x2="12" y2="8"/>
            </svg>
          </div>
          <div>
            <h1 className="push-title">Notificaciones Push</h1>
            <p className="push-subtitle">Controla quién recibe alertas en la app móvil</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="push-stats">
        <div className="push-stat-card">
          <span className="push-stat-num">{usuarios.length}</span>
          <span className="push-stat-label">Usuarios totales</span>
        </div>
        <div className="push-stat-card push-stat-card--green">
          <span className="push-stat-num">{totalActivos}</span>
          <span className="push-stat-label">Con push activo</span>
        </div>
        <div className="push-stat-card push-stat-card--purple">
          <span className="push-stat-num">{totalConDispositivo}</span>
          <span className="push-stat-label">Con dispositivo registrado</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="push-filters">
        <div className="push-search-wrap">
          <svg className="push-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="push-search"
            type="text"
            placeholder="Buscar por nombre o email..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        <div className="push-rol-tabs">
          {ROLES.map(r => (
            <button
              key={r.value}
              className={`push-rol-tab ${rolFiltro === r.value ? 'active' : ''}`}
              style={rolFiltro === r.value && r.bg ? { background: r.bg, color: r.color, borderColor: r.color + '66' } : {}}
              onClick={() => setRolFiltro(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {isAdmin && comunidades.length > 1 && (
          <select
            className="push-com-select"
            value={comunidadFiltro}
            onChange={e => setComunidadFiltro(e.target.value)}
          >
            <option value="">Todas las comunidades</option>
            {comunidades.map(c => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        )}
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="push-loading">
          <div className="push-spinner" />
          <span>Cargando usuarios...</span>
        </div>
      ) : usuarios.length === 0 ? (
        <div className="push-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <p>No se encontraron usuarios</p>
        </div>
      ) : (
        <div className="push-groups">
          {(rolFiltro
            ? rolesConUsuarios.filter(r => r === rolFiltro)
            : rolesConUsuarios
          ).map(rol => {
            const lista = porRol[rol];
            const info = ROL_LABELS[rol];
            const todosActivos = lista.every(u => u.push_habilitado);
            const ningunoActivo = lista.every(u => !u.push_habilitado);

            return (
              <div key={rol} className="push-group">
                <div className="push-group-header">
                  <div className="push-group-title">
                    <span
                      className="push-group-badge"
                      style={{ background: info.bg, color: info.color, borderColor: info.border }}
                    >
                      {info.label}
                    </span>
                    <span className="push-group-count">{lista.length} usuarios</span>
                  </div>
                  <div className="push-bulk-actions">
                    <button
                      className="push-bulk-btn push-bulk-btn--on"
                      disabled={todosActivos || bulkLoading === `${rol}-true`}
                      onClick={() => handleBulkRol(rol, true)}
                    >
                      {bulkLoading === `${rol}-true` ? '...' : 'Activar todos'}
                    </button>
                    <button
                      className="push-bulk-btn push-bulk-btn--off"
                      disabled={ningunoActivo || bulkLoading === `${rol}-false`}
                      onClick={() => handleBulkRol(rol, false)}
                    >
                      {bulkLoading === `${rol}-false` ? '...' : 'Desactivar todos'}
                    </button>
                  </div>
                </div>

                <div className="push-user-list">
                  {lista.map(u => (
                    <div key={u.id} className={`push-user-row ${!u.push_habilitado ? 'push-user-row--off' : ''}`}>
                      <Avatar nombre={u.nombre} apellidos={u.apellidos} />
                      <div className="push-user-info">
                        <span className="push-user-name">
                          {u.nombre} {u.apellidos}
                        </span>
                        <span className="push-user-meta">
                          {u.email}
                          {u.piso && <> · {u.piso}</>}
                          {u.comunidad_nombre && isAdmin && <> · {u.comunidad_nombre}</>}
                        </span>
                      </div>
                      <div className="push-user-device">
                        {u.tiene_token ? (
                          <span className="push-device-badge push-device-badge--yes">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <rect x="5" y="2" width="14" height="20" rx="2"/>
                              <line x1="12" y1="18" x2="12.01" y2="18"/>
                            </svg>
                            Dispositivo
                          </span>
                        ) : (
                          <span className="push-device-badge push-device-badge--no">Sin app</span>
                        )}
                      </div>
                      <div className="push-user-toggle">
                        <Toggle
                          checked={!!u.push_habilitado}
                          onChange={val => handleToggle(u.id, val)}
                          disabled={toggling.has(u.id)}
                        />
                        <span className={`push-toggle-label ${u.push_habilitado ? 'push-toggle-label--on' : 'push-toggle-label--off'}`}>
                          {u.push_habilitado ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
